import type { AnchorHTMLAttributes, ButtonHTMLAttributes, ReactNode } from "react";
import { Link, type LinkProps } from "react-router-dom";
import styles from "./Button.module.css";

type Variant = "primary" | "secondary" | "ghost";
type Size = "md" | "lg";

type CommonProps = {
  variant?: Variant;
  size?: Size;
  children: ReactNode;
};

type ButtonAsButton = CommonProps &
  ButtonHTMLAttributes<HTMLButtonElement> & {
    as?: "button";
  };

type ButtonAsLink = CommonProps &
  Omit<LinkProps, "to"> & {
    as: "link";
    to: LinkProps["to"];
  };

type ButtonAsAnchor = CommonProps &
  AnchorHTMLAttributes<HTMLAnchorElement> & {
    as: "a";
    href: string;
  };

type ButtonProps = ButtonAsButton | ButtonAsLink | ButtonAsAnchor;

function classes(variant: Variant, size: Size, extra?: string) {
  return [styles.btn, styles[variant], styles[size], extra].filter(Boolean).join(" ");
}

export default function Button(props: ButtonProps) {
  const { variant = "primary", size = "md", children } = props;

  if (props.as === "link") {
    const { className, to, ...rest } = props;
    return (
      <Link to={to} className={classes(variant, size, className)} {...rest}>
        {children}
      </Link>
    );
  }

  if (props.as === "a") {
    const { className, href, ...rest } = props;
    return (
      <a
        href={href}
        target={href.startsWith("http") ? "_blank" : undefined}
        rel={href.startsWith("http") ? "noopener noreferrer" : undefined}
        className={classes(variant, size, className)}
        {...rest}
      >
        {children}
      </a>
    );
  }

  const { className, ...rest } = props;
  return (
    <button className={classes(variant, size, className)} {...rest}>
      {children}
    </button>
  );
}
